/**
 * JWT utility functions for token management and security
 */

export interface JWTPayload {
  exp: number;
  iat: number;
  email: string;
  id?: number;
  fullName?: string;
  roles?: string[];
  isActive?: boolean;
  [key: string]: unknown;
}

/**
 * Decode JWT token without verification (client-side only)
 * WARNING: This does NOT verify the signature. Always verify on backend.
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired (strict check, no buffer)
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const now = Date.now();

  return now >= expirationTime;
}

/**
 * Check if JWT token should be refreshed (expires within 5 minutes)
 */
export function shouldRefreshTokenSoon(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // Add 5 minute buffer to refresh before actual expiration
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  const now = Date.now();

  return now >= (expirationTime - bufferTime);
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpirationTime(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return payload.exp * 1000; // Convert to milliseconds
}

/**
 * Get time until token expiration in milliseconds
 */
export function getTimeUntilExpiration(token: string): number | null {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) {
    return null;
  }

  const now = Date.now();
  return Math.max(0, expirationTime - now);
}

/**
 * Check if token should be refreshed (expires within 5 minutes)
 */
export function shouldRefreshToken(token: string): boolean {
  return shouldRefreshTokenSoon(token);
}
